import { addQueryString } from '../../helpers/url'
import { log } from '../../logger'
import { PaginationMeta } from '../../models/pagination'
import { apiClient } from './client'

export interface TaxConfigurationResponse {
  id: number
  taxName: string
  taxType: string
  taxDescription: string
  country: string
  states: string[]
  zipCodes: string[]
  taxRatePercent: string
  taxRateKwh: string
  isFreeSessionExempt: boolean
  isTaxPercentOnKwh: boolean
  powerThresholdMin: string | null
  powerThresholdMax: string | null
  minimumTransactionAmount: string | null
  taxNameDisplay: string | null
  taxNotesDisplay: string | null
  taxAuthorityContact: string | null
  regulatoryReference: string | null
  status: 'active' | 'inactive' | 'draft'
  approvedByName: string | null
  createdById: number
  createdAt: string
  lastModifiedAt: string
}

export interface TaxConfigurationsResponse {
  data: TaxConfigurationResponse[]
  pagination: PaginationMeta
}

export interface CreateTaxConfigurationPayload {
  taxName: string
  taxType: string
  taxDescription: string
  country: string
  states?: string[]
  zipCodes?: string[]
  taxRatePercent?: string
  taxRateKwh?: string
  isFreeSessionExempt?: boolean
  isTaxPercentOnKwh?: boolean
  powerThresholdMin?: string | null
  powerThresholdMax?: string | null
  minimumTransactionAmount?: string | null
  taxNameDisplay?: string | null
  taxNotesDisplay?: string | null
  taxAuthorityContact?: string | null
  regulatoryReference?: string | null
  approvedByName?: string | null
  status: 'active' | 'inactive' | 'draft'
}

export interface TaxConfigurationFilters {
  taxName?: string | string[]
  locationType?: 'state' | 'zip'
  location?: string | string[]
  status?: 'active' | 'inactive' | 'draft' | ('active' | 'inactive' | 'draft')[]
}

export async function GetTaxConfigurations(
  page: number = 1,
  perPage: number = 20,
  sortByLastUpdated?: 'desc' | 'asc' | 'none',
  filters?: TaxConfigurationFilters,
): Promise<TaxConfigurationsResponse> {
  let url = '/driver_tax_configurations'
  
  const queryParams: Record<string, string> = {
    page: page.toString(),
    per_page: perPage.toString(),
  }
  
  if (sortByLastUpdated && sortByLastUpdated !== 'none') {
    queryParams.sort_by_last_updated = sortByLastUpdated
  }
  
  if (filters) {
    if (filters.taxName) {
      if (Array.isArray(filters.taxName)) {
        queryParams.tax_name = filters.taxName.join(',')
      } else {
        queryParams.tax_name = filters.taxName
      }
    }
    if (filters.locationType && filters.location) {
      if (filters.locationType === 'state') {
        if (Array.isArray(filters.location)) {
          queryParams.state = filters.location.join(',')
        } else {
          queryParams.state = filters.location
        }
      } else {
        if (Array.isArray(filters.location)) {
          queryParams.zip_code = filters.location.join(',')
        } else {
          queryParams.zip_code = filters.location
        }
      }
    }
    if (filters.status) {
      if (Array.isArray(filters.status)) {
        queryParams.status = filters.status.join(',')
      } else {
        queryParams.status = filters.status
      }
    }
  }
  
  url = addQueryString(url, queryParams)
  
  log('fetching tax configurations URL', { url })
  const response = await apiClient<{ data: TaxConfigurationResponse[]; pagination: PaginationMeta }>(url, {})
  
  // API now returns camelCase directly
  return {
    data: response.data,
    pagination: response.pagination,
  }
}

export async function GetTaxConfigurationById(id: number): Promise<TaxConfigurationResponse> {
  const url = `/driver_tax_configurations/${id}`
  
  log('fetching tax configuration by ID', { url })
  const response = await apiClient<TaxConfigurationResponse>(url, {})
  
  // API now returns camelCase directly
  return response
}

export async function CreateTaxConfiguration(
  payload: CreateTaxConfigurationPayload,
): Promise<TaxConfigurationResponse> {
  const url = '/driver_tax_configurations'
  
  // Map frontend "archive" to backend "inactive"
  const backendPayload: any = {
    ...payload,
    status: (payload.status as any) === 'archive' ? 'inactive' : payload.status,
  }
  
  log('creating tax configuration', { url, payload: backendPayload })
  // API now accepts camelCase in request body
  const response = await apiClient<TaxConfigurationResponse>(url, {
    method: 'POST',
    body: JSON.stringify(backendPayload),
  })
  
  // Map backend "inactive" to frontend "archive"
  return {
    ...response,
    status: response.status === 'inactive' ? 'archive' : response.status,
  } as TaxConfigurationResponse
}

export async function UpdateTaxConfiguration(
  id: number,
  payload: CreateTaxConfigurationPayload,
): Promise<TaxConfigurationResponse> {
  const url = `/driver_tax_configurations/${id}`
  
  // Map frontend "archive" to backend "inactive"
  const backendPayload: any = {
    ...payload,
    status: (payload.status as any) === 'archive' ? 'inactive' : payload.status,
  }
  
  log('updating tax configuration', { url, payload: backendPayload })
  // API now accepts camelCase in request body
  const response = await apiClient<TaxConfigurationResponse>(url, {
    method: 'PATCH',
    body: JSON.stringify(backendPayload),
  })
  
  // Map backend "inactive" to frontend "archive"
  return {
    ...response,
    status: response.status === 'inactive' ? 'archive' : response.status,
  } as TaxConfigurationResponse
}

export async function GetTaxConfigurationsByLocation(
  state?: string,
  zipCode?: string,
  status: 'active' | 'inactive' | 'draft' = 'active',
): Promise<TaxConfigurationResponse[]> {
  let url = '/driver_tax_configurations'
  
  const queryParams: Record<string, string> = {}
  if (state) {
    queryParams.state = state
  }
  if (zipCode) {
    queryParams.zip_code = zipCode
  }
  if (status) {
    queryParams.status = status
  }
  
  url = addQueryString(url, queryParams)
  
  log('fetching tax configurations by location', { url })
  const response = await apiClient<{ data: TaxConfigurationResponse[]; pagination: PaginationMeta }>(url, {})
  
  // API now returns camelCase directly
  return response.data
}

export async function SearchTaxNames(term: string): Promise<string[]> {
  const url = addQueryString('/driver_tax_configurations/search', { tax_name: term })
  
  log('searching tax names', { url })
  const response = await apiClient<string[]>(url, {})
  
  return response
}

export async function SearchZipCodes(term: string): Promise<Array<{ zip_code: string }>> {
  const url = addQueryString('/driver_tax_configurations/search', { zip_code: term })
  
  log('searching zip codes', { url })
  const response = await apiClient<Array<{ zip_code: string }>>(url, {})
  
  return response
}

